import styles from './Home.module.css';

import classNames from 'classnames';
import React, { useRef } from 'react';
import { FunnelX, CarSimple, ArrowCounterClockwise } from '@phosphor-icons/react';

import { useCatalogRoute } from 'src/hooks/useCatalogRoute';
import { useCatalogPaging } from 'src/hooks/useCatalogPaging';
import { useQueryScreenState } from 'src/hooks/useQueryScreenState';
import { useCatalogFilterPanel } from 'src/hooks/useCatalogFilterPanel';

import { catalogHead } from 'src/utils/pageHead';
import { saleCardSlots } from 'src/utils/listingCardSlots';
import { toSaleParams, buildCatalogPath } from 'src/utils/catalogUrl';

import { useListingsPage } from 'src/queries';

import { Loader } from 'src/components/Loader';
import { Stack } from 'src/components/themed/Stack';
import { Pagination } from 'src/components/Pagination';
import { ListingCard } from 'src/components/ListingCard';
import { DocumentHead } from 'src/components/DocumentHead';
import { Typography } from 'src/components/themed/Typography';
import { RegisterBanner } from 'src/components/RegisterBanner';
import { VerticalSwitch } from 'src/components/VerticalSwitch';
import { useAssistantEntry } from 'src/components/AssistantPanel';
import { ActionButton } from 'src/components/themed/ActionButton';
import { EmptyState, RequestFailureState } from 'src/components/EmptyState';
import { ListingGrid, CatalogBody, CatalogPage, ListingCount, CatalogTopRow } from 'src/components/ListingGrid';

interface HomeProps {
  /** Show the soft-migration register nudge (anonymous visitors only). */
  showRegisterBanner?: boolean;
}

/**
 * The sale catalog — at `/`, and at `/buy/:make?/:model?` with its facets and page in the address
 * (storefront-catalog spec §4.1). `/` stays the home page and is not redirected: any link this
 * screen builds leads under `/buy`. The filters are a column beside the grid on a laptop and a
 * sheet behind the funnel on a phone; either way they only ever change the address.
 */
export const Home = ({ showRegisterBanner }: HomeProps) => {
  const route = useCatalogRoute('buy');
  const scrollContainer = useRef<HTMLDivElement>(null);
  // A make the reference does not know matches nothing: the feed is not asked.
  const query = useListingsPage(toSaleParams(route), { enabled: route.ready && !route.unknownSlug });
  const { data, isRefetching, isPlaceholderData, refetch } = query;
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  // Loading, an explained failure, or the list — a 500 and no network used to render as the
  // same "Cars for sale" with nothing under it as a genuinely empty market (BUG-04, BUG-06).
  const state = useQueryScreenState(query);
  useCatalogPaging(route, isPlaceholderData ? undefined : data?.total, scrollContainer);

  const filters = useCatalogFilterPanel('buy', route);
  // The assistant (api#425, app#397): "Ask GAC AI" beside Buy / Rent, its window on demand.
  const assistant = useAssistantEntry('buy');
  const resetFilters = filters.reset;
  // The cars and the lists of the filter are asked together: one count, renewed as one.
  const reload = () => {
    refetch();
    filters.refresh();
  };

  return (
    <>
      <DocumentHead
        {...catalogHead({
          vertical: 'buy',
          makeName: route.make?.name,
          modelName: route.model?.name,
          makeSlug: route.makeSlug,
          modelSlug: route.modelSlug,
          filters: route.filters,
          page: route.page,
          total: state === 'ready' && !isPlaceholderData ? data?.total : undefined,
          firstCar: items[0],
        })}
      />
      {filters.sheet}
      {assistant.window}
      <div ref={scrollContainer} className={classNames('container', styles.container)}>
        <CatalogPage gap={2} p={4} pb={30}>
          <CatalogTopRow>
            <VerticalSwitch
              active="buy"
              buyTo={buildCatalogPath({ vertical: 'buy' })}
              rentTo={buildCatalogPath({ vertical: 'rent' })}
            />
            {assistant.pill}
          </CatalogTopRow>
          <Stack
            width="100%"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            pl={4}
          >
            <Stack gap={1}>
              <Typography as="h1" fontWeight="600" variant="title3" color="white">
                Cars for sale
              </Typography>
              {route.ready && !route.unknownSlug && state === 'ready' && total > 0 ? (
                <ListingCount total={total} />
              ) : null}
            </Stack>
            <Stack flexDirection="row" gap={2}>
              {/* The funnel opens the sheet — a phone's filters. A laptop has them beside the grid. */}
              {filters.funnel}
              <ActionButton onClick={reload} label="Refresh the list">
                <ArrowCounterClockwise size={24} color="white" />
              </ActionButton>
            </Stack>
          </Stack>
          {showRegisterBanner ? <RegisterBanner /> : null}
          <CatalogBody>
            {filters.sidebar}
            {/* The refresh button shows the spinner. A page link does not: while the next page is on
                its way the previous one stays on screen (a placeholder), and the grid never drops. */}
            {!route.ready ? (
              route.lookupState === 'loading' || route.lookupState === 'ready' ? (
                <Loader />
              ) : (
                <RequestFailureState kind={route.lookupState} onRetry={filters.retryLookup} />
              )
            ) : route.unknownSlug ? (
              <NoMatch onReset={resetFilters} />
            ) : state === 'loading' || (isRefetching && !isPlaceholderData) ? (
              <Loader />
            ) : state !== 'ready' ? (
              <RequestFailureState kind={state} onRetry={reload} />
            ) : items.length === 0 ? (
              // Empty with a filter is "nothing matched" and has a way out; empty without one is
              // the market itself (BUG-07). The two used to be the same blank space.
              route.filtered ? (
                <NoMatch onReset={resetFilters} />
              ) : (
                <EmptyState
                  icon={<CarSimple size={48} />}
                  title="No cars for sale yet"
                  description="Dealers are adding cars. Check back soon."
                />
              )
            ) : (
              <Stack width="100%" gap={4}>
                <ListingGrid>
                  {items.map((item) => (
                    <ListingCard key={item.id} {...saleCardSlots(item)} />
                  ))}
                </ListingGrid>
                <Pagination total={total} page={route.page} hrefFor={(page) => route.pathTo({ page })} />
              </Stack>
            )}
          </CatalogBody>
        </CatalogPage>
      </div>
    </>
  );
};

/** Nothing matched the make, the model or the facets: say so, and offer the whole catalog back. */
const NoMatch = ({ onReset }: { onReset: () => void }) => (
  <EmptyState
    icon={<FunnelX size={48} />}
    title="No cars match these filters"
    description="Try other filters, or reset them."
    action={{ label: 'Reset filters', onClick: onReset }}
  />
);
